import { useEffect } from "react";

import { useMachine } from "@xstate/react";
import isEqual from "lodash/isEqual";
import { Interpreter } from "xstate";

import { invoicePreviewQuery_account_invoicePreview_paymentIntent as PaymentIntentType } from "@zapier/checkout/esm/graphql-types";
import { UseQuery } from "@zapier/checkout/esm/types";

import {
  isAuthenticatedQuery,
  useIsAuthenticated,
} from "../useIsAuthenticated";
import { usePrevious } from "../usePrevious";
import {
  Context,
  Event,
  EventObject,
  State,
  Tag,
  paymentIntentsMachine,
} from "./paymentIntentsMachine";

/**
 * Whenever a payment intent changes, we tell the payment intents machine to
 * reset itself with the new payment intent in context.
 */
const useResetMachine = (
  paymentIntent: PaymentIntentType | null | undefined,
  send: Interpreter<Context, any, EventObject>["send"]
) => {
  const previousPaymentIntent = usePrevious(paymentIntent);
  const paymentIntentDidChange = !isEqual(previousPaymentIntent, paymentIntent);

  useEffect(() => {
    /**
     * When the payment intent changes (for example, if the user changes any details about their plan, we generate a new payment intent),
     * we need to reset the payment intent state machine with those new details. We do that
     * by setting the PI context with the new one, and resetting back to the initial state.
     */
    if (paymentIntentDidChange) {
      send(Event.resetWithPaymentIntent, { paymentIntent });
      /**
       * After the resetting to the initial state, we re-initialize the machine (which will go through the Stripe SDK init if needed, etc).
       */
      send(Event.init);
    }
  }, [paymentIntentDidChange, send, paymentIntent]);
};

const usePaymentIntentsMachine = (
  paymentIntent: PaymentIntentType | null | undefined,
  onChangePlan: () => Promise<void | null>,
  useQuery: UseQuery
) => {
  const { client } = useIsAuthenticated({
    useQuery,
  });

  // @ts-ignore - types don't match here (until we can upgrade @xstate/react)
  const piMachine = useMachine(paymentIntentsMachine, {
    context: {
      paymentIntent,
      error: undefined,
    },
    services: {
      submitPayment: onChangePlan,
      queryAuthenticationStatus: async () => {
        /**
         * The ideal solution here was to use `useLazyQuery`, but the API for
         * that hook in v2 does not return any results from the query
         * (returns void). The v3 API does return the results from the query.
         *
         * Because of this limitation, we have to use `client.query` to get the
         * results of isAuthenticatedQuery for use in the state machine.
         */
        const { data } = await client.query({
          query: isAuthenticatedQuery,
          fetchPolicy: "network-only",
        });
        return data;
      },
    },
  });

  const [state, send] = piMachine;

  useResetMachine(paymentIntent, send);

  return {
    isLoading: state.hasTag(Tag.loading),
    canSubmit: state.hasTag(Tag.submittable),
    hasFatalError: state.matches(State.errorInitializing),
    hasPaymentConfirmationError: state.matches(
      State.errorConfirmingPaymentIntent
    ),
    retryPayment: () => send(Event.retry),
    submit: () => send(Event.submit),
    state,
    send,
  };
};

export { usePaymentIntentsMachine };
